import {useCallback, useEffect, useId, useRef} from 'react';

import eventMessagesService from '@/services/events/eventMessagesService';

/**
 * Tracks which hook instances are requesting the menu to be hidden.
 * The menu is only shown again when every instance that hid it has released its hold.
 */
const hidingSources = new Set<string>();

const useMenuVisibility = () => {
    const id = useId();
    const isHidingRef = useRef(false);

    const hideMenu = useCallback(() => {
        if (!isHidingRef.current) {
            isHidingRef.current = true;
            hidingSources.add(id);
        }
        eventMessagesService.sendHideMenu();
    }, [id]);

    const showMenu = useCallback(() => {
        if (isHidingRef.current) {
            isHidingRef.current = false;
            hidingSources.delete(id);
        }
        if (hidingSources.size === 0) {
            eventMessagesService.sendShowMenu();
        }
    }, [id]);

    useEffect(() => {
        return () => {
            if (isHidingRef.current) {
                isHidingRef.current = false;
                hidingSources.delete(id);
                if (hidingSources.size === 0) {
                    eventMessagesService.sendShowMenu();
                }
            }
        };
    }, [id]);

    return {hideMenu, showMenu};
};

export default useMenuVisibility;
